import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  Input,
  Output,
  EventEmitter,
  PLATFORM_ID,
  SimpleChanges,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { NotificationService } from '../../shared/services/notification.service';
import { secureStorage } from '../../shared/services/secure-storage.service';

interface BreadcrumbItem {
  label: string;
  url?: string;
  active?: boolean;
}

@Component({
  selector: 'app-breadcrum',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './breadcrum.component.html',
  styleUrl: './breadcrum.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumComponent implements OnInit {
  @Input() data: any;
  @Input() partType?: 'Scrap Part' | 'Used Part';
  @Input() categoryName?: string;
  @Input() productName?: string;
  @Input() isProductList?: boolean = false;
  @Output() breadcrumbClick = new EventEmitter<{
    label: string;
    url: any;
    index: number;
  }>();

  breadcrumbdata: any = [];
  breadcrumbs: any = [];
  hidebreadcrumb = false;
  dynamicBreadcrumbs: BreadcrumbItem[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private localStorage: secureStorage,
    private behaviorService: BehaviourSubjectService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.breadcrumbs = changes['data'].currentValue;
      this.generateDynamicBreadcrumbs();
      this.cdr.markForCheck();
    }
    if (
      changes['partType'] ||
      changes['categoryName'] ||
      changes['productName'] ||
      changes['isProductList']
    ) {
      this.generateDynamicBreadcrumbs();
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    this.breadcrumbs = this.data;
    this.getBreadCrumb();
    this.generateDynamicBreadcrumbs();
    this.cdr.markForCheck();
  }

  generateDynamicBreadcrumbs(): void {
    if (this.isProductList) {
      this.dynamicBreadcrumbs = [{ label: 'Home', url: '/home' }];

      // Add part type (Scrap Part / Used Part)
      if (this.partType) {
        const partTypeLabel = 'Scrap Part'; //this.partType === 'Scrap Part' ? 'Scrap' : 'Used Part';
        this.dynamicBreadcrumbs.push({
          label: partTypeLabel,
          url: `/product-list`,
        });
      }

      // Add category if available
      if (this.categoryName) {
        this.dynamicBreadcrumbs.push({
          label: this.categoryName,
          active: true,
        });
      }
    } else if (!this.isProductList && (this.partType || this.categoryName || this.productName)) {
      // Product detail page breadcrumbs
      this.dynamicBreadcrumbs = [{ label: 'Home', url: '/home' }];

      // Add part type (Scrap Part / Used Part)
      if (this.partType) {
        const partTypeLabel =
          this.partType === 'Scrap Part' ? 'Scrap Part' : 'Used Part';
        this.dynamicBreadcrumbs.push({
          label: partTypeLabel,
          url: `/product-list`,
        });
      }

      // Add category if available
      if (this.categoryName) {
        this.dynamicBreadcrumbs.push({
          label: this.categoryName,
          url: `/product-list`,
        });
      }

      // Add product name as the final breadcrumb if available
      if (this.productName) {
        this.dynamicBreadcrumbs.push({
          label: this.productName,
          active: true,
        });
      } else if (this.dynamicBreadcrumbs.length > 0) {
        this.dynamicBreadcrumbs[this.dynamicBreadcrumbs.length - 1].active = true;
      }
    } else {
      // Use existing breadcrumb logic for other pages
      this.dynamicBreadcrumbs = this.breadcrumbs;
    }
    this.cdr.markForCheck();
  }

  getBreadCrumb() {
    if (this.breadcrumbs == undefined || this.breadcrumbs == null) {
      this.hidebreadcrumb = true;
      this.cdr.markForCheck();
    }
  }

  private navigateByUrl(url: string): Promise<boolean> {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
      return this.router.navigate([url]);
    }

    const path = url.substring(0, queryIndex);
    const queryString = url.substring(queryIndex + 1);
    const queryParams: Record<string, string> = {};

    for (const param of queryString.split('&')) {
      const [key, ...rest] = param.split('=');
      if (key) {
        queryParams[decodeURIComponent(key)] = decodeURIComponent(
          rest.join('='),
        );
      }
    }

    return this.router.navigate([path], { queryParams });
  }

  GotoRoute(breadcrumb: { label: string; url: any }, index: number) {
    if (isPlatformBrowser(this.platformId)) {
      // Emit the breadcrumb click event for parent component to handle
      this.breadcrumbClick.emit({
        label: breadcrumb.label,
        url: breadcrumb.url,
        index: index,
      });

      // Special handling for Cart and HOME breadcrumbs - let parent component handle navigation
      if (breadcrumb.label === 'Cart' || breadcrumb.label === 'HOME') {
        return; // Don't navigate, let parent handle it
      }

      // Only navigate if there's a valid URL
      if (breadcrumb.url) {
        this.breadcrumbs.splice(index + 1, this.breadcrumbs?.length - index);
        // this.notificationService.info(`Navigating to ${breadcrumb.label}`);

        this.navigateByUrl(breadcrumb.url).then(
          (success) => {
            if (success) {
              this.behaviorService.isSmart.next(false);
              this.cdr.markForCheck();
            } else {
                // this.notificationService.error(
                //   'Navigation failed. Please try again.',
                // );
            }
          },
          (error) => {
            console.error('Navigation error:', error);
            // this.notificationService.error(
            //   'Navigation error. Please try again.',
            // );
          },
        );
      } else {
        // this.notificationService.info('This breadcrumb has no navigation link');
      }
    }
  }

  getCursor(label: string) {
    let labels = label?.toLowerCase();
    // if(labels == 'trucks' || labels == 'buses & vans' || labels == 'scv pickups' || labels == 'brand'){
    //   return 'default';
    // }
  }

  onBreadcrumbClick(breadcrumb: BreadcrumbItem, index: number): void {
    if (breadcrumb.active) {
      // this.notificationService.info('You are already on this page');
      return;
    }

    if (breadcrumb.url) {
      // this.notificationService.info(`Navigating to ${breadcrumb.label}`);

      this.navigateByUrl(breadcrumb.url).then(
        (success) => {
          if (!success) {
            // this.notificationService.error(
            //   'Navigation failed. Please try again.',
            // );
          }
        },
        (error) => {
          console.error('Navigation error:', error);
          // this.notificationService.error('Navigation error. Please try again.');
        },
      );
    } else {
      // this.notificationService.info('This breadcrumb has no navigation link');
    }
  }
}
